<template>
  <div class="ProfitabilityCalculatorCmp">
    <div class="container shadow-hard">
      <div class="outerGrid">
        <div class="title"><slot name="title" /></div>
        <div class="innerGrid">
          <div
            v-for="(col, idx) in [
              firstColFields,
              secondColFields,
              thirdColFields,
            ]"
            :key="`col-${idx}`"
            :class="`colGrid-${idx}`"
          >
            <div v-for="field in col" :key="field.id">
              <div
                v-if="
                  editablefields.includes(field.id) &&
                  config[`suffix_${field.id}`]
                "
                class="suffix a-caption"
                :style="`bottom:${offset}px`"
              >
                {{ config[`suffix_${field.id}`] }}
              </div>
              <input-text-cmp-extended
                v-if="editablefields.includes(field.id)"
                :value="_self[field.id]"
                :data-name="field.id"
                :data-placeholder="config[`placeholder_${field.id}`]"
                @input="_self[field.id] = $event"
              >
                <template #label>
                  <slot :name="`label_${field.id}`" />
                </template>
              </input-text-cmp-extended>
              <input-text-cmp-extended
                v-else
                :value="_self[field.id]"
                :data-name="field.id"
                :data-placeholder="config[`placeholder_${field.id}`]"
                :class="field.type"
                :data-readonly="true"
                :data-disabled="true"
              >
                <template #label>
                  <slot :name="`label_${field.id}`" />
                </template>
              </input-text-cmp-extended>
            </div>
          </div>
        </div>
        <div class="a-support note">
          <slot name="label_note_additional_ingredients" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { castPropToType } from '@/helpers/cms-support';
export default {
  name: 'ProfitabilityCalculatorCmp',
  props: {
    dataConfig: {
      type: [Object, String],
      required: true,
    },
  },
  setup() {
    return { castPropToType };
  },
  data() {
    return {
      offset: 0,
      editablefields: [
        'cost_per_case',
        'oz_per_case',
        'serving_size',
        'cost_per_additional_ingredients',
        'menu_price',
      ],
      computedFields: [
        'cost_per_oz',
        'cost_per_fry_serving',
        'total_plate_cost',
        'food_cost',
        'profit_per_case',
        'profit_per_plate',
      ],
      firstColFields: [
        { id: 'cost_per_case', type: 'calculatorInput' },
        { id: 'cost_per_oz', type: 'calculatorComputed' },
        { id: 'cost_per_fry_serving', type: 'calculatorComputed' },
        { id: 'total_plate_cost', type: 'calculatorComputed' },
      ],
      secondColFields: [
        { id: 'oz_per_case', type: 'calculatorInput' },
        { id: 'serving_size', type: 'calculatorInput' },
        { id: 'cost_per_additional_ingredients', type: 'calculatorInput' },
        { id: 'menu_price', type: 'calculatorInput' },
      ],
      thirdColFields: [
        { id: 'food_cost', type: 'calculatorResult' },
        { id: 'profit_per_plate', type: 'calculatorResult' },
        { id: 'profit_per_case', type: 'calculatorResult' },
      ],
      cost_per_case_real_value: void 0,
      oz_per_case_real_value: void 0,
      serving_size_real_value: void 0,
      cost_per_additional_ingredients_real_value: void 0,
      menu_price_real_value: void 0,
      cost_per_oz_real_value: void 0,
      cost_per_fry_serving_real_value: void 0,
      total_plate_cost_real_value: void 0,
      food_cost_real_value: void 0,
      profit_per_case_real_value: void 0,
      profit_per_plate_real_value: void 0,
    };
  },
  computed: {
    cost_per_oz() {
      /** Cost per case / Oz per case */
      if (!this.cost_per_case_real_value || !this.oz_per_case_real_value) {
        return this.config.placeholder_cost_per_oz;
      } else {
        const value = (
          Number(this.cost_per_case_real_value) /
          Number(this.oz_per_case_real_value)
        ).toFixed(2);

        this.valueSetter('cost_per_oz', value);
        return this.valueGetter('cost_per_oz');
      }
    },
    cost_per_fry_serving() {
      /** Cost per oz x Serving Size (oz) */
      if (!this.cost_per_oz_real_value || !this.serving_size_real_value) {
        return this.config.placeholder_cost_per_fry_serving;
      } else {
        const value = (
          Number(this.cost_per_oz_real_value) *
          Number(this.serving_size_real_value)
        ).toFixed(2);
        this.valueSetter('cost_per_fry_serving', value);
        return this.valueGetter('cost_per_fry_serving');
      }
    },
    total_plate_cost() {
      /**Cost per fry serving + Cost per additional ingredients */
      if (
        !this.cost_per_fry_serving_real_value ||
        !this.cost_per_additional_ingredients_real_value
      ) {
        return this.config.placeholder_total_plate_cost;
      } else {
        const value = (
          Number(this.cost_per_fry_serving_real_value) +
          Number(this.cost_per_additional_ingredients_real_value)
        ).toFixed(2);
        this.valueSetter('total_plate_cost', value);
        return this.valueGetter('total_plate_cost');
      }
    },
    food_cost() {
      /** Total Plate Cost / Menu Price * 100 */
      if (!this.total_plate_cost_real_value || !this.menu_price_real_value) {
        return this.config.placeholder_food_cost;
      } else {
        const value = (
          (Number(this.total_plate_cost_real_value) /
            Number(this.menu_price_real_value)) *
          100
        ).toFixed(2);

        this.valueSetter('food_cost', value);
        return this.valueGetter('food_cost');
      }
    },
    profit_per_case() {
      /** peofit per plate * (oz per case / serving size)  */
      if (
        !this.profit_per_plate_real_value ||
        !this.oz_per_case_real_value ||
        !this.serving_size_real_value
      ) {
        return this.config.placeholder_profit_per_case;
      } else {
        const value = (
          Number(this.profit_per_plate_real_value) *
          (Number(this.oz_per_case_real_value) /
            Number(this.serving_size_real_value))
        ).toFixed(2);

        this.valueSetter('profit_per_case', value);
        return this.valueGetter('profit_per_case');
      }
    },
    profit_per_plate() {
      /**Menu price - Total plate cost */
      if (!this.menu_price_real_value || !this.total_plate_cost_real_value) {
        return this.config.placeholder_profit_per_plate;
      } else {
        const value = (
          Number(this.menu_price_real_value) -
          Number(this.total_plate_cost_real_value)
        ).toFixed(2);

        this.valueSetter('profit_per_plate', value);
        return this.valueGetter('profit_per_plate');
      }
    },
    config() {
      return this.dataConfig
        ? this.castPropToType(this.dataConfig, 'object')
        : null;
    },
  },
  beforeCreate() {
    const gettersToCreate = [
      'oz_per_case',
      'cost_per_case',
      'serving_size',
      'cost_per_additional_ingredients',
      'menu_price',
    ];
    gettersToCreate.forEach((key) => {
      this.$options.computed[key] = {
        get() {
          return this.valueGetter(key);
        },
        set(newValue) {
          this.valueSetter(key, newValue);
        },
      };
    });
  },
  mounted() {
    window.addEventListener('resize', this.updateOffset);
    const updateOffsetFirstTime = () => {
      try {
        this.updateOffset();
      } catch (e) {
        setTimeout(updateOffsetFirstTime, 100);
      }
    };
    updateOffsetFirstTime();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateOffset);
  },
  methods: {
    updateOffset() {
      const $firstSuffix = this.$el.getElementsByClassName('suffix')[0];
      const $firstParent = $firstSuffix.parentElement;
      const $firstInput = $firstSuffix.nextSibling;
      const firstParentHeight = $firstParent.getBoundingClientRect().height;
      const firstInputHeight = $firstInput.getBoundingClientRect().height;
      const delta = firstParentHeight - firstInputHeight;
      this.offset = parseFloat((delta + 8).toFixed());
    },
    valueGetter(attribute) {
      const realValueAttribute = `${attribute}_real_value`;
      const formatAttribute = `format_${attribute}`;
      const realValue = this[realValueAttribute];
      const format = this.config[formatAttribute];
      if (!realValue) {
        return void 0;
      } else {
        if (typeof format !== 'string' || !format.includes('{}')) {
          return String(realValue);
        }

        return format.replace('{}', realValue);
      }
    },
    valueSetter(attribute, newValue) {
      const realValueAttribute = `${attribute}_real_value`;
      const formatAttribute = `format_${attribute}`;
      const format = this.config[formatAttribute];
      this[realValueAttribute] = newValue
        .replace(format.replace('{}', ''), '')
        .trim();
    },
  },
};
</script>

<style lang="scss" scoped>
.ProfitabilityCalculatorCmp {
  .title {
    ::v-deep * {
      font-weight: 700 !important;
    }
  }

  --bfp-gap: 27px;
  --bfp-small-gap: 16px;
  .InputTextCmpExtended.InputTextCmp.form-group__wrapper {
    --form-element-input-padding-top: 3px;
    --form-element-input-padding-right: 60px;
    --form-element-input-padding-bottom: 3px;
    --form-element-input-padding-left: 5px;
  }

  .InputTextCmpExtended.InputTextCmp.form-group__wrapper {
    ::v-deep .form-group .form__label {
      --form-element-label-color: #{$color-white};
      --form-element-label-disabled-color: #{$color-white};
    }
  }
  .InputTextCmpExtended.InputTextCmp.form-group__wrapper.calculatorInput {
    ::v-deep .form-control__wrapper {
      border-radius: 4px;
      --form-element-input-padding-top: 3px;
      --form-element-input-padding-right: 15px;
      --form-element-input-padding-bottom: 3px;
      --form-element-input-padding-left: 15px;
    }
    ::v-deep .form-group .form__label {
    }
  }
  .InputTextCmpExtended.InputTextCmp.form-group__wrapper.calculatorComputed {
    ::v-deep .form-control__wrapper {
      --form-element-bg-color: transparent;
      --form-element-input-disabled-border-color: transparent;
      --form-element-input-disabled-color: white;
      --form-element-disabled-bg-color: transparent;
      --form-element-input-padding-top: 8px;
      --form-element-input-padding-right: 0;
      --form-element-input-padding-bottom: 8px;
      --form-element-input-padding-left: 0;
      border-top: 1px solid white;
      border-bottom: 1px solid white;

      input {
        font-weight: 700 !important;
      }
    }
  }
  .InputTextCmpExtended.InputTextCmp.form-group__wrapper.calculatorResult {
    --form-element-padding: 0;
    --form-element-bg-color: transparent;
    --form-element-input-border-radius: 0;
    --form-element-input-border-width: 0;
    --form-element-input-border-color: transparent;
    --form-element-input-padding-top: 0;
    --form-element-input-padding-right: 0;
    --form-element-input-padding-bottom: 0;
    --form-element-input-padding-left: 0;
    --form-element-placeholder-color: white;
    --form-element-input-hover-border-color: transparent;
    --form-element-placeholder-hover-color: white;
    --form-element-input-focus-border-color: transparent;
    --form-element-disabled-bg-color: transparent;
    --form-element-input-disabled-border-color: transparent;
    --form-element-input-disabled-color: white;
    --form-element-placeholder-disabled-color: white;
    --form-element-error-color: transparent;
    --form-element-helper-color: transparent;
    ::v-deep .form-group .form__label {
      --form-element-label-padding: 0;
      @include responsive-typography-properties($font-primary, a-paragraph-3);
      font-weight: 700;
    }
    ::v-deep .form-group.disabled .form-control__wrapper .form-control {
      @include responsive-typography-properties($font-secondary, n-paragraph-1);
      font-weight: 700;
    }
  }
  .container {
    padding: 16px;
    background: $color-blue;
    color: $color-white;
    border-radius: 18px;
    max-width: 740px;
  }
  @include media-breakpoint-down(md) {
    .note {
      margin: 16px 0 0;
    }
    .colGrid-0,
    .colGrid-1,
    .colGrid-2 {
      display: block;
      > div {
        margin: 16px 0 0;
        position: relative;
        .suffix {
          position: absolute;
          bottom: 6px;
          right: 10px;
          color: black;
          z-index: 10;
        }
      }
    }
    .colGrid-2 {
      .InputTextCmpExtended.InputTextCmp.form-group__wrapper.calculatorResult {
        ::v-deep .form-group {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          align-items: center;
          .form-control__wrapper {
            text-align: right;
          }
        }
      }
      > div {
        position: relative;
        padding: 8px 0;
        margin: 0;
        border-bottom: 1px solid white;
        &:first-child {
          border-top: 1px solid white;
        }
      }
    }
  }
  @include media-breakpoint-up(md) {
    .container {
      padding: var(--bfp-gap);
    }
    .outerGrid {
      max-height: 500px;
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      grid-template-rows: auto 1fr auto;
      gap: var(--bfp-gap);
      align-items: center;
    }
    .innerGrid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(1, 1fr);
      gap: var(--bfp-gap);
    }
    .colGrid-0,
    .colGrid-1 {
      display: grid;
      gap: var(--bfp-small-gap);
      > div {
        position: relative;
        .suffix {
          position: absolute;
          bottom: 12px;
          right: 10px;
          color: black;
          z-index: 10;
        }
      }
    }
    .colGrid-2 {
      padding: 0 0 0 25px;
      display: grid;
      grid-template-columns: repeat(1, 1fr);
      grid-template-rows: repeat(3, 1fr);
      // gap: var(--bfp-gap);
      // padding: var(--bfp-gap) 0;
      > div {
        position: relative;
        padding: 20px 0;
        border-bottom: 1px solid white;
        text-align: center;
        &:first-child {
          border-top: 1px solid white;
        }
      }
    }
  }
}
</style>
